<script lang="ts" setup>
import { computed } from 'vue';

import { CheckmarkIcon, MinusIcon, SolidCheckIcon } from '@/components/svg';

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    bgColor?: string;
    borderColor?: string;
    borderWidth?: string;
    svgFill?: string;
    rounded?: string | boolean;
    variant?: 'outline' | 'filled';
    testId?: string;
    disabled?: boolean;
    name?: string;
    id?: string;
    indeterminate?: boolean;
  }>(),
  {
    bgColor: 'transparent',
    borderColor: 'var(--ch-blue-100)',
    borderWidth: '1px',
    svgFill: 'var(--ch-blue-100)',
    rounded: '3px',
    variant: 'outline',
    testId: 'custom-checkbox',
    indeterminate: false,
  }
);

const emit = defineEmits<(e: 'update:modelValue', value: boolean) => void>();

const onChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  emit('update:modelValue', target.checked);
};

const getBorderRadius = () => {
  if (typeof props.rounded === 'string') {
    return props.rounded;
  } else if (props.rounded === true) {
    return '100%';
  } else {
    return '0px';
  }
};

const spanStyles = computed(() => ({
  background: props.bgColor ?? 'transparent',
  border: `${props.borderWidth} solid ${props.borderColor ?? 'var(--ch-blue-100)'}`,
  borderRadius: getBorderRadius(),
}))

const variantStyles = computed(() => {
  const isSelectedOrIndeterminate = props.modelValue || props.indeterminate;

  return {
    background: props.variant === 'filled' && isSelectedOrIndeterminate ? 'var(--ch-blue-100)' : props.bgColor,
    border: `${props.borderWidth} solid ${props.variant === 'filled' && isSelectedOrIndeterminate ? 'var(--ch-blue-100)' : props.borderColor}`,
  }
});
</script>

<template>
  <label :data-testid="testId">
    <input
      type="checkbox"
      :checked="modelValue"
      :id="id"
      :name="name ?? id"
      :disabled="disabled"
      :indeterminate="indeterminate"
      @change="onChange"
    />

    <span
      class="flex-center"
      :style="{ ...spanStyles, ...variantStyles }"
    >
      <MinusIcon
        v-if="indeterminate"
        class="dash-icon"
        :fill="svgFill"
      />

      <SolidCheckIcon
        v-else-if="modelValue && variant === 'filled'"
        class="check-icon"
        :fill="svgFill"
      />

      <CheckmarkIcon
        v-else-if="modelValue"
        class="check-icon"
        :fill="svgFill"
      />
    </span>
  </label>
</template>

<style scoped>
label {
  width: 1rem;
  height: 1rem;
}

label span {
  width: 100%;
  height: 100%;
  transition: 0.1s transform ease-in-out, 0.1s -webkit-transform ease-in-out;
  -webkit-transition: 0.1s -webkit-transform ease-in-out;
  -o-transition: 0.1s transform ease-in-out;
  cursor: pointer;
}

label input {
  display: none;
}

label span .dash-icon,
label span .check-icon {
  display: block; 
  width: 0.6rem;
  height: 0.6rem;
  margin: auto;
}

label input ~ span .check-icon {
  display: none;
}

label input:checked ~ span .check-icon {
  display: block;
}
</style>
